import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type AdminApp } from './api.js';

const CATEGORIES = [
  'audio-video',
  'business',
  'communication',
  'development',
  'education',
  'games',
  'graphics',
  'health',
  'news',
  'productivity',
  'security',
  'system',
  'utilities',
];

/**
 * Fixes the fields the importer guesses: the category comes from winget tags,
 * which is approximate, and the name often needs tidying.
 */
export function EditAppForm({
  app,
  onClose,
  onSaved,
}: {
  app: AdminApp;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(app.name);
  const [category, setCategory] = useState(app.category);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateApp(app.id, {
        ...(name.trim() !== app.name ? { name: name.trim() } : {}),
        ...(category !== app.category ? { category } : {}),
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Правка приложения</h2>
        <p className="mt-1 text-xs text-muted">{app.id}</p>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted">Название</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted">Категория</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            >
              {CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-muted">
              Импорт ставит категорию по тегам winget. Ваш выбор он больше не перезапишет.
            </span>
          </label>
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={save} disabled={busy || name.trim().length === 0}>
            {busy ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </div>
  );
}
